import { Component } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { isEqual } from 'lodash';

/**
 * Component that automatically adds AI context when mounted and removes it when unmounted.
 * This is a non-hook alternative to useAiContext for class components or when you prefer component-based approach.
 *
 * @example
 * // Basic usage
 * <AiContext
 *   type="device"
 *   name="router-01"
 *   context="Router details: IP 192.168.1.1, Status: Active"
 * />
 *
 * @example
 * // With suggestions
 * <AiContext
 *   type="interface"
 *   name="eth0"
 *   context="Interface eth0 on router-01: Speed 1Gbps, Status: Up"
 *   suggestions={[
 *     'Show bandwidth utilization for this interface',
 *     'Check for errors on this interface'
 *   ]}
 * />
 *
 * @example
 * // With model object
 * <AiContext
 *   type="device"
 *   name={device.get('device_name')}
 *   context={`Device: ${device.get('device_name')}, Status: ${device.statusDisplay}`}
 *   model={device}
 * />
 */
@inject('$kaizen', '$auth')
@observer
class AiContext extends Component {
  static propTypes = {
    /** The type of context (e.g., 'device', 'interface', 'cloud_vpc') */
    type: PropTypes.string.isRequired,

    /** The unique name/identifier for this context */
    name: PropTypes.string.isRequired,

    /** The detailed context information */
    context: PropTypes.string.isRequired,

    /** Optional model object associated with this context */
    model: PropTypes.object,

    /** Optional array of suggested prompts */
    suggestions: PropTypes.arrayOf(PropTypes.string)
  };

  static defaultProps = {
    model: null,
    suggestions: []
  };

  componentDidMount() {
    this.addContext();
  }

  componentDidUpdate(prevProps) {
    const { type, name, context } = this.props;

    // Re-add context if key properties changed
    if (prevProps.type !== type || prevProps.name !== name || !isEqual(prevProps.context, context)) {
      this.removeContext(prevProps);
      this.addContext();
    }
  }

  componentWillUnmount() {
    this.removeContext();
  }

  addContext = () => {
    const { $auth, $kaizen, type, name, context, placeholder, model, suggestions } = this.props;

    if (!$auth.canUseAiTools || !$auth.hasPermission('kaizen', { overrideForSudo: false })) {
      return;
    }

    const contextObject = {
      type,
      name,
      context,
      placeholder
    };

    // Add optional properties if provided
    if (model) {
      contextObject.model = model;
    }

    if (suggestions && suggestions.length > 0) {
      contextObject.suggestions = suggestions;
    }

    $kaizen.addChatContext(contextObject);
  };

  removeContext = (props = this.props) => {
    const { $kaizen } = this.props;

    const contextObject = {
      type: props.type,
      name: props.name,
      context: props.context
    };

    $kaizen.removeChatContext(contextObject);
  };

  render() {
    // This component only manages context - it doesn't render anything
    return null;
  }
}

export default AiContext;
